import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Box } from '@mui/material';

// Shared full-screen photo viewer for employee-session pictures.
//
// The audit/detail lightboxes used to rely on the browser's
// `touchAction: 'pinch-zoom'`, but that never fires inside a fullscreen
// MUI Dialog (the dialog is its own scroll/containing block, not the
// visual viewport), so the image opened fit-to-screen and could not be
// zoomed further. This component implements real gesture zoom with
// Pointer Events — pinch, two-finger pan, single-finger pan when zoomed,
// mouse wheel, and double-tap/double-click to toggle — entirely in JS so
// it works the same on tablets and desktop. The image is always shown
// with `object-fit: contain`, so the complete captured photo is visible
// (no cropped angles).

const MIN_SCALE = 1;
const MAX_SCALE = 6;
const DOUBLE_TAP_MS = 300;

export default function ZoomablePhoto({ src, alt = '', topOffset = 56 }) {
  const containerRef = useRef(null);
  const imgRef = useRef(null);
  const [view, setView] = useState({ scale: 1, tx: 0, ty: 0 });
  const viewRef = useRef(view);
  viewRef.current = view;

  // Gesture bookkeeping that must not trigger re-renders.
  const gesture = useRef({
    pointers: new Map(),
    lastDist: 0,
    lastMid: null,
    lastTap: 0,
  });

  // Reset zoom whenever a different photo is shown.
  useEffect(() => {
    setView({ scale: 1, tx: 0, ty: 0 });
  }, [src]);

  // Keep the panned image within the bounds of the actual (object-fit
  // contained) picture, so it can never be flung completely off-screen.
  const clamp = useCallback((scale, tx, ty) => {
    const container = containerRef.current;
    const img = imgRef.current;
    if (!container || !img || !img.naturalWidth) return { tx, ty };
    const cw = container.clientWidth;
    const ch = container.clientHeight;
    // Base displayed size with object-fit: contain at scale 1.
    const fit = Math.min(cw / img.naturalWidth, ch / img.naturalHeight);
    const baseW = img.naturalWidth * fit;
    const baseH = img.naturalHeight * fit;
    const maxX = Math.max(0, (baseW * scale - cw) / 2);
    const maxY = Math.max(0, (baseH * scale - ch) / 2);
    return {
      tx: Math.min(maxX, Math.max(-maxX, tx)),
      ty: Math.min(maxY, Math.max(-maxY, ty)),
    };
  }, []);

  // Zoom toward a screen point so the picture grows under the fingers /
  // cursor instead of jumping to the center.
  const zoomAround = useCallback(
    (clientX, clientY, nextScale) => {
      const container = containerRef.current;
      if (!container) return;
      const rect = container.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const midX = clientX - cx;
      const midY = clientY - cy;
      const { scale, tx, ty } = viewRef.current;
      const clamped = Math.min(MAX_SCALE, Math.max(MIN_SCALE, nextScale));
      const ratio = clamped / scale;
      const ntx = midX - (midX - tx) * ratio;
      const nty = midY - (midY - ty) * ratio;
      const bounded = clamp(clamped, ntx, nty);
      setView({ scale: clamped, tx: bounded.tx, ty: bounded.ty });
    },
    [clamp]
  );

  const dist = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
  const mid = (a, b) => ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });

  const onPointerDown = useCallback((e) => {
    const g = gesture.current;
    if (e.currentTarget.setPointerCapture) {
      e.currentTarget.setPointerCapture(e.pointerId);
    }
    g.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (g.pointers.size === 2) {
      const [p1, p2] = [...g.pointers.values()];
      g.lastDist = dist(p1, p2);
      g.lastMid = mid(p1, p2);
    }
  }, []);

  const onPointerMove = useCallback(
    (e) => {
      const g = gesture.current;
      if (!g.pointers.has(e.pointerId)) return;
      g.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      const pts = [...g.pointers.values()];

      if (pts.length >= 2) {
        // Pinch zoom + two-finger pan.
        const [p1, p2] = pts;
        const d = dist(p1, p2);
        const m = mid(p1, p2);
        if (g.lastDist > 0) {
          const { scale } = viewRef.current;
          zoomAround(m.x, m.y, scale * (d / g.lastDist));
          if (g.lastMid) {
            const { scale: s, tx, ty } = viewRef.current;
            const bounded = clamp(
              s,
              tx + (m.x - g.lastMid.x),
              ty + (m.y - g.lastMid.y)
            );
            setView({ scale: s, tx: bounded.tx, ty: bounded.ty });
          }
        }
        g.lastDist = d;
        g.lastMid = m;
        e.preventDefault();
      } else if (pts.length === 1 && viewRef.current.scale > 1) {
        // Single-finger pan once zoomed in.
        const prev = g.lastMid || pts[0];
        const { scale, tx, ty } = viewRef.current;
        const bounded = clamp(
          scale,
          tx + (pts[0].x - prev.x),
          ty + (pts[0].y - prev.y)
        );
        setView({ scale, tx: bounded.tx, ty: bounded.ty });
        g.lastMid = pts[0];
        e.preventDefault();
      }
    },
    [zoomAround, clamp]
  );

  const endPointer = useCallback((e) => {
    const g = gesture.current;
    g.pointers.delete(e.pointerId);
    if (g.pointers.size < 2) {
      g.lastDist = 0;
      // Keep panning smooth if one finger remains.
      g.lastMid = g.pointers.size === 1 ? [...g.pointers.values()][0] : null;
    }
  }, []);

  const onWheel = useCallback(
    (e) => {
      e.preventDefault();
      const { scale } = viewRef.current;
      const factor = e.deltaY < 0 ? 1.15 : 1 / 1.15;
      zoomAround(e.clientX, e.clientY, scale * factor);
    },
    [zoomAround]
  );

  // Double-tap / double-click toggles between fit and 2.5x.
  const handleDoubleZoom = useCallback(
    (clientX, clientY) => {
      const { scale } = viewRef.current;
      if (scale > 1.05) {
        setView({ scale: 1, tx: 0, ty: 0 });
      } else {
        zoomAround(clientX, clientY, 2.5);
      }
    },
    [zoomAround]
  );

  const onPointerUp = useCallback(
    (e) => {
      const g = gesture.current;
      const wasSingle = g.pointers.size === 1;
      endPointer(e);
      if (wasSingle && e.pointerType === 'touch') {
        const now = e.timeStamp;
        if (now - g.lastTap < DOUBLE_TAP_MS) {
          handleDoubleZoom(e.clientX, e.clientY);
          g.lastTap = 0;
        } else {
          g.lastTap = now;
        }
      }
    },
    [endPointer, handleDoubleZoom]
  );

  return (
    <Box
      ref={containerRef}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={endPointer}
      onWheel={onWheel}
      onDoubleClick={(e) => handleDoubleZoom(e.clientX, e.clientY)}
      sx={{
        position: 'absolute',
        top: `${topOffset}px`,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        background: '#000',
        touchAction: 'none',
        userSelect: 'none',
      }}
    >
      {src && (
        <img
          ref={imgRef}
          src={src}
          alt={alt}
          draggable={false}
          style={{
            maxWidth: '100%',
            maxHeight: '100%',
            width: 'auto',
            height: 'auto',
            objectFit: 'contain',
            display: 'block',
            transform: `translate3d(${view.tx}px, ${view.ty}px, 0) scale(${view.scale})`,
            transformOrigin: 'center center',
            transition: gesture.current.pointers.size
              ? 'none'
              : 'transform 0.12s ease-out',
            userSelect: 'none',
            WebkitUserDrag: 'none',
            cursor: view.scale > 1 ? 'grab' : 'zoom-in',
          }}
        />
      )}
    </Box>
  );
}
